// LaundryItemMaster.js  Master CRUD for Laundry items.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Switch, FormControlLabel, Chip, Snackbar, Alert,
  CircularProgress, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import { laundryApi, ctx } from './modulesApi';

const EMPTY = {
  laundryItemId: 0, companyId: 0, hotelId: 0,
  itemName: '', category: '', washType: 'Normal',
  price: 0, gstPercent: 18, hsnCode: '', estimatedDeliveryHours: 24, isActive: true,
};
const WASH = ['Normal', 'DryClean', 'IronOnly', 'Express'];
const CATEGORIES = ['Mens', 'Womens', 'Linen', 'Other'];

export default function LaundryItemMaster() {
  const { companyId, hotelId, userId, currency } = ctx();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await laundryApi.listItems(hotelId, { page: page + 1, pageSize: rowsPerPage, search });
      setItems(r.data.items || []); setTotal(r.data.totalCount || 0);
    } catch { notify('Failed to load', 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage]);

  const onSave = async () => {
    if (!form.itemName) return notify('Item name required', 'warning');
    try {
      await laundryApi.saveItem({ ...form, companyId, hotelId }, userId);
      notify('Saved'); setOpen(false); setForm(EMPTY); load();
    } catch { notify('Save failed', 'error'); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await laundryApi.deleteItem(id, userId); notify('Deleted'); load(); }
    catch { notify('Failed', 'error'); }
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Card>
        <CardContent>
          <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
            <Box display='flex' alignItems='center' gap={1}>
              <LocalLaundryServiceIcon color='primary' />
              <Typography variant='h5'>Laundry Item Master</Typography>
            </Box>
            <Button variant='contained' startIcon={<AddIcon />}
              onClick={() => { setForm({ ...EMPTY, companyId, hotelId }); setOpen(true); }}>
              Add Item
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size='small' label='Search' value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()} />
            </Grid>
          </Grid>

          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell><TableCell>Category</TableCell>
                  <TableCell>Wash Type</TableCell><TableCell align='right'>Price</TableCell>
                  <TableCell align='right'>GST %</TableCell><TableCell>HSN Code</TableCell>
                  <TableCell align='right'>ETA (hrs)</TableCell>
                  <TableCell>Status</TableCell><TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (<TableRow><TableCell colSpan={9} align='center'><CircularProgress size={24} /></TableCell></TableRow>)}
                {!loading && items.length === 0 && (<TableRow><TableCell colSpan={9} align='center'>No items</TableCell></TableRow>)}
                {items.map((x) => (
                  <TableRow key={x.laundryItemId} hover>
                    <TableCell>{x.itemName}</TableCell>
                    <TableCell>{x.category}</TableCell>
                    <TableCell><Chip size='small' label={x.washType} /></TableCell>
                    <TableCell align='right'>{currency} {Number(x.price).toFixed(2)}</TableCell>
                    <TableCell align='right'>{x.gstPercent}</TableCell>
                    <TableCell>{x.hsnCode || '-'}</TableCell>
                    <TableCell align='right'>{x.estimatedDeliveryHours}</TableCell>
                    <TableCell><Chip size='small' label={x.isActive ? 'Active' : 'Inactive'} color={x.isActive ? 'success' : 'default'} /></TableCell>
                    <TableCell align='right'>
                      <IconButton size='small' onClick={() => { setForm({ ...x }); setOpen(true); }}><EditIcon /></IconButton>
                      <IconButton size='small' color='error' onClick={() => onDelete(x.laundryItemId)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component='div' count={total} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} />
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{form.laundryItemId ? 'Edit' : 'Add'} Laundry Item</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label='Item Name' value={form.itemName}
                onChange={(e) => setForm({ ...form, itemName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label='Category' value={form.category || ''}
                onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label='Wash Type' value={form.washType}
                onChange={(e) => setForm({ ...form, washType: e.target.value })}>
                {WASH.map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type='number' label='Price' value={form.price}
              onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type='number' label='GST %' value={form.gstPercent}
              onChange={(e) => setForm({ ...form, gstPercent: parseFloat(e.target.value) || 0 })} /></Grid>
            <Grid item xs={6} sm={6}><TextField fullWidth label='HSN Code' value={form.hsnCode || ''}
              onChange={(e) => setForm({ ...form, hsnCode: e.target.value })} /></Grid>
            <Grid item xs={6} sm={6}><TextField fullWidth type='number' label='ETA (hours)' value={form.estimatedDeliveryHours}
              onChange={(e) => setForm({ ...form, estimatedDeliveryHours: parseInt(e.target.value, 10) || 0 })} /></Grid>
            <Grid item xs={12}><FormControlLabel control={
              <Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            } label='Active' /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
